import  {
  useEffect,
  useState,
  useCallback,
} from "react";
import { Pagination } from "@mui/material";
import axiosInstance from "../../../../components/axiosInstance";
import { API_URLS } from "../../../../apiUrl";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AccountsReport = () => {
  const getToday = () => new Date().toISOString().split("T")[0];

  const getOneMonthAgo = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  };

  const getNextDay = (dateStr: any) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  };

  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState(getOneMonthAgo());
  const [endDate, setEndDate] = useState(getToday());
  const [search, setSearch] = useState("");

  const [activeIndex, setActiveIndex] = useState<number | null>(null);           // Other Cashier popup
  const [depositBreakdownIndex, setDepositBreakdownIndex] = useState<number | null>(null); // Deposit Breakdown popup

  const limit = 50;

const fetchData = useCallback(
  async (
    pageNumber: number,
    searchValue = search,
    sDate = startDate,
    eDate = endDate
  ) => {
    setLoading(true);
    try {
      const response = await fetch(API_URLS.cashierdeposit, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          start_date: sDate,
          end_date: eDate,
          collector_summary: "fetch",
          page: pageNumber,
          limit: limit,
          search: searchValue,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      const res = await response.json();
      setData(res.data?.data?.rows || []);
      setTotalPages(res.data?.data?.pagination?.lastPage || 1);
    } catch (err) {
      // Show actual error
      toast.error(err instanceof Error ? err.message : "Something went wrong ❌");
    } finally {
      setLoading(false);
    }
  },
  [search, startDate, endDate, limit]
);

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchData(1, search);
    }, 500);
    return () => clearTimeout(delay);
  }, [search, fetchData]);

  const handlePageChange = (event: any, value: number) => {
    setPage(value);
    fetchData(value);
  };

  const openDepositBreakdown = (index: number) => {
    setActiveIndex(null);
    setDepositBreakdownIndex(depositBreakdownIndex === index ? null : index);
  };

  const openOtherCashier = (index: number) => {
    setDepositBreakdownIndex(null);
    setActiveIndex(activeIndex === index ? null : index);
  };

  const closeAll = () => {
    setActiveIndex(null);
    setDepositBreakdownIndex(null);
  };

  return (
    <div className="p-5 bg-white rounded-lg shadow relative">
      <ToastContainer position="top-right" autoClose={3000} />

      <h2 className="text-lg font-semibold mb-4">Accounts Report</h2>

      {/* FILTER */}
      <div className="flex items-end justify-between mb-4 gap-4">
        <div className="flex items-end gap-3">
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Start</label>
            <input
              type="date"
              value={startDate}
              max={getToday()}
              onChange={(e) => {
                const newStart = e.target.value;
                setStartDate(newStart);
                if (newStart >= endDate) {
                  setEndDate(getNextDay(newStart));
                }
              }}
              className="border px-2 py-1.5 rounded-md text-sm w-32"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">End</label>
            <input
              type="date"
              value={endDate}
              min={getNextDay(startDate)}
              max={getToday()}
              onChange={(e) => setEndDate(e.target.value)}
              className="border px-2 py-1.5 rounded-md text-sm w-32"
            />
          </div>

          <button
            onClick={() => {
              const newStart = getOneMonthAgo();
              const newEnd = getToday();
              setStartDate(newStart);
              setEndDate(newEnd);
              setSearch("");
              setPage(1);
              fetchData(1, "", newStart, newEnd);
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-2 py-2 rounded-md text-sm transition"
          >
            Reset <i className="ri-restart-line"></i>
          </button>
        </div>

        <input
          type="text"
          placeholder="Search collector..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border px-3 py-2 rounded-md text-sm w-64"
        />
      </div>

      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
          <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 border">SL</th>
              <th className="p-2 border">Field Collector</th>
              <th className="p-2 border">Collected</th>

              <th className="p-2 border">Total Amount</th>

              <th className="p-2 border">Mapped</th>
              <th className="p-2 border">Other Cashier Deposit</th>
              <th className="p-2 border">Available Balance (₹)</th>
            </tr>
          </thead>

          <tbody>
            {data.map((item, index) => {
              const mapped = Number(item.mapped_by_collector || 0);
              const otherCashierAmount = Number(
                item.deposited_by_other_cashiers?.total_amount_added_by_other_cashiers || 0
              );
              const breakdown = item.deposit_breakdown || [];

              return (
                <tr key={index} className="text-center">
                  <td className="p-2 border">
                    {(page - 1) * limit + index + 1}
                  </td>

                  <td className="p-2 border">{item.collector_name}</td>

                  <td className="p-2 border">
                   <div className="flex items-center justify-center">
  <span className="w-[90px] text-right">₹ {Number(item.total_deposited)}</span>

  <span
    onClick={() => openDepositBreakdown(index)}
    title="View deposit breakdown"
                        className="ml-2 w-4 h-4 flex items-center justify-center rounded-full bg-cyan-500 text-white text-[10px] cursor-pointer hover:bg-cyan-600 transition flex-shrink-0"
  >
    i
  </span>
</div>

                    {depositBreakdownIndex === index && (
                      <>
                        <div
                          className="fixed inset-0 bg-black bg-opacity-20 z-40"
                          onClick={closeAll}
                        />
                        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white shadow-xl rounded-lg p-4 w-[480px] z-50">
                        <div className="flex justify-between items-center mb-3">
  <div className="text-left">
    <h3 className="font-semibold text-sm">Deposit Breakdown</h3>
    <p className="text-sm text-gray-400 mt-1">
      Field Collector: {item.collector_name}
    </p>
  </div>

  <button
    onClick={closeAll}
    className="text-gray-400 hover:text-gray-600 text-lg leading-none"
  >
    ✕
  </button>
</div>

                          <div className="max-h-64 overflow-y-auto border rounded">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-100 sticky top-0">
                                <tr>
                                  <th className="p-2 border">#</th>
                                  <th className="p-2 border">Date</th>
                                  <th className="p-2 border">Amount (₹)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {breakdown.length === 0 ? (
                                  <tr>
                                    <td colSpan={3} className="text-center p-3 text-gray-400">
                                      No breakdown data
                                    </td>
                                  </tr>
                                ) : (
                                  breakdown.map((b: any, i: number) => (
                                    <tr key={i} className="text-center hover:bg-gray-50">
                                      <td className="p-2 border text-gray-400">{i + 1}</td>
                                        <td className="p-2 border text-gray-500">
                                        {new Date(b.date).toLocaleDateString("en-GB", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                        })}
                                      </td>
                                      <td className="p-2 border font-medium text-green-700">
                                        ₹ {b.amount}
                                      </td>
                                    
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>

                          {/* Total row */}
                          <div className="mt-2 flex justify-end">
                            <span className="text-xs text-gray-500 mr-2">Total:</span>
                            <span className="text-xs font-semibold text-green-700">
                              ₹ {breakdown.reduce((sum: number, b: any) => sum + Number(b.amount), 0)}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </td>


<td className="p-2 border">
 ₹ {item.total_amount_deposited_till_now ?? "--"}
</td>
                  <td className="p-2 border">₹ {mapped}</td>


                  <td className="p-2 border">
                    <div className="flex items-center justify-center">
                      <span className="w-[90px] text-right">₹ {otherCashierAmount}</span>
                      <span
                        onClick={() => openOtherCashier(index)}
                        className="ml-2 w-4 h-4 flex items-center justify-center rounded-full bg-cyan-500 text-white text-[10px] cursor-pointer hover:bg-cyan-600 transition flex-shrink-0"
                      >
                        i
                      </span>
                    </div>

                    {activeIndex === index && (
                      <>
                        <div
                          className="fixed inset-0 bg-black bg-opacity-20 z-40"
                          onClick={closeAll}
                        />
                        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white shadow-xl rounded-lg p-4 w-[500px] z-50">
                         <div className="flex justify-between items-center mb-3">
  <div className="text-left">
    <h3 className="font-semibold text-sm">Cashier Details</h3>
    <p className="text-xs text-gray-400">
      Field Collector: {item.collector_name}
    </p>
  </div>

  <button
    onClick={closeAll}
    className="text-gray-400 hover:text-gray-600 text-lg leading-none"
  >
    ✕
  </button>
</div>

                          <div className="max-h-60 overflow-y-auto border rounded">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-100 sticky top-0">
                                <tr>
                                  <th className="p-2 border">Name</th>
                                  <th className="p-2 border">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.deposited_by_other_cashiers?.cashiers?.length === 0 ? (
                                  <tr>
                                    <td colSpan={3} className="text-center p-3 text-gray-400">
                                      No Data
                                    </td>
                                  </tr>
                                ) : (
                                  item.deposited_by_other_cashiers.cashiers.map(
                                    (c: any, i: number) => (
                                      <tr key={i} className="text-center hover:bg-gray-50">
                                        <td className="p-2 border">{c.name || "-"}</td>
                                        <td className="p-2 border font-medium text-green-700">
                                          ₹ {c.amount}
                                        </td>
                                       
                                      </tr>
                                    )
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    )}
                  </td>

                  <td className="p-2 border font-semibold">
                    <span className={Number(item.available_balance) < 0 ? "text-red-600" : "text-green-600"}>
                      ₹ {Number(item.available_balance)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-center mt-4">
        <Pagination count={totalPages} page={page} onChange={handlePageChange} />
      </div>
    </div>
  );
};

export default AccountsReport;